import React, { useState, useEffect } from "react";
import "./CardsMatchUpForm.css";
import CircularCountdownTimer from "../CircularCountdownTimer/CircularCountdownTimer";

// Use Unicode fruit symbols for the memory cards
// Extended fruit symbols to 20
const fruitSymbols = [
  "🍎",
  "🍌",
  "🍊",
  "🍓",
  "🍉",
  "🍇",
  "🍒",
  "🍍",
  "🥭",
  "🍑",
  "🍈",
  "🍋",
  "🍏",
  "🥝",
  "🍐",
  "🍅",
  "🥥",
  "🍆",
  "🍠",
  "🍒",
];

// Function to shuffle the cards
const shuffleArray = (array) => {
  return array
    .concat(array) // duplicate the images to get pairs
    .sort(() => Math.random() - 0.5); // shuffle
};

// Function to calculate the best row and column configuration
function calculateGridDimensions(numPairs) {
  const totalCards = numPairs * 2;
  let bestRows = 1;
  let bestCols = totalCards;

  // Try to find two factors of totalCards that are as close as possible
  for (let rows = 1; rows <= Math.sqrt(totalCards); rows++) {
    if (totalCards % rows === 0) {
      const cols = totalCards / rows;
      if (Math.abs(rows - cols) < Math.abs(bestRows - bestCols)) {
        bestRows = rows;
        bestCols = cols;
      }
    }
  }
  return { rows: bestRows, cols: bestCols };
}

const CardsMatchUpForm = ({
  initialFruitCount = 3,
  timeToMemorize = 10,
  initialPuzzleCount = 5,
}) => {
  const [numFruits, setNumFruits] = useState(initialFruitCount);
  const [cards, setCards] = useState(
    shuffleArray(fruitSymbols.slice(0, numFruits))
  );
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [clickCount, setClickCount] = useState(0);
  const [puzzleStarted, setPuzzleStarted] = useState(false);
  const [rows, setRows] = useState(2);
  const [cols, setCols] = useState(2);
  const [timerVisible, setTimerVisible] = useState(true);
  const [mistakes, setMistakes] = useState(0);
  const [correctPairs, setCorrectPairs] = useState(0);
  const [puzzleCount, setPuzzleCount] = useState(initialPuzzleCount);
  const [timer, setTimer] = useState(timeToMemorize);

  // Handle card click
  const handleCardClick = (index) => {
    if (
      flippedCards.length === 2 ||
      matchedCards.includes(index) ||
      flippedCards.includes(index)
    )
      return;

    setClickCount(clickCount + 1); // Increment click count
    setFlippedCards([...flippedCards, index]); // Add clicked card to flipped cards

    if (flippedCards.length === 1) {
      // Check if the two flipped cards match
      const firstIndex = flippedCards[0];
      const secondIndex = index;
      if (cards[firstIndex] === cards[secondIndex]) {
        setMatchedCards([...matchedCards, firstIndex, secondIndex]);
        setCorrectPairs(correctPairs + 1);
      } else {
        setMistakes(mistakes + 1);
      }

      // Reset flipped cards after 1 second
      setTimeout(() => {
        setFlippedCards([]);
      }, 1000);
    }
  };

  useEffect(() => {
    if (correctPairs === numFruits) {
      //This is the end of the puzzle, set up a new puzzle
      setPuzzleCount(puzzleCount - 1);
    }
  }, [correctPairs]);

  useEffect(() => {
    if (!puzzleStarted) {
      return;
    }

    if (puzzleCount > 0) {
      if (mistakes > 0) {
        setNumFruits(numFruits - 1);
      } else {
        setNumFruits(numFruits + 1);
      }
    }
  }, [puzzleCount]);

  useEffect(() => {
    setInitialState();
    setTimer(numFruits * 2);
  }, [numFruits]);

  const handleTimeIsUp = () => {
    setTimerVisible(false);
    setFlippedCards([]);
    setPuzzleStarted(true);
  };

  const setInitialState = () => {
    setCards(shuffleArray(fruitSymbols.slice(0, numFruits)));
    setFlippedCards([]);
    setMatchedCards([]);
    setClickCount(0);
    setMistakes(0);
    setPuzzleStarted(false);
    setCorrectPairs(0);

    const { rows, cols } = calculateGridDimensions(numFruits);
    setRows(rows);
    setCols(cols);

    setTimerVisible(true);
  };

  return (
    <div className="App">
      <h1>Memory Card Game</h1>

      <p>Fruits: {numFruits}</p>
      <p>Correct pairs: {correctPairs}</p>
      <p>Clicks: {clickCount}</p>
      <p>Mistakes: {mistakes}</p>

      {/* Timer display */}
      <CircularCountdownTimer
        timeInSeconds={timer}
        onTimeIsUp={handleTimeIsUp}
        visible={timerVisible}
      />
      {/* Card grid */}
      <div
        className="fruit-grid"
        style={{
          gridTemplateColumns: `repeat(${cols}, 100px)`,
          gridTemplateRows: `repeat(${rows}, 100px)`,
        }}
      >
        {cards.map((fruit, index) => (
          <div
            key={index}
            className={`card ${
              flippedCards.includes(index) || matchedCards.includes(index)
                ? "flipped"
                : ""
            }`}
            onClick={() => handleCardClick(index)}
          >
            {flippedCards.includes(index) ||
            matchedCards.includes(index) ||
            !puzzleStarted ? (
              <span className="fruit-symbol">{fruit}</span>
            ) : (
              <div className="question-mark">?</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CardsMatchUpForm;
